// 스페셜 몬스터 컨트롤러(STAGE-WORLD-PRD 4절): 황금 고블린(도망치는 보상 몹)과 챔피언(접두어 붙은 승격 정예).
// 일반 스테이지(stageKind normal)에서만, 판 시작에 결정적 난수(게임 씨앗 + 판 번호 + 스테이지)로 한 번 굴려 한 판 최대 1마리.
// 보스판(n-5·n-10)·자유 입장·마을은 어떤 경우에도 계획이 없다. 이 컨트롤러는 game.rng를 쓰지 않는다(굴림이 없을 때와 다른 난수 흐름을 만들지 않는다).
// 결선: mobs.ts(spawn 훅·황금 고블린 이동·챔피언 속도/공격력)·combat.ts(처치 보상 한 줄)·flow.ts(클리어 직전 황금 고블린 출현 훅·결과 항목 한 줄)·game.ts(판 시작·세계 스텝).
// 프레임 루프 안에서 새 객체를 만들지 않는다(고정 배열·원시값만).
import { MOB_STATS } from '../shared/config';
import { SPECIAL, type DifficultyKey } from '../shared/content/special';
import { REGIONS } from '../shared/regions';
import { hashSeed, nextRand, seedRng, type SeedRng } from '../shared/gear';
import { MobKind, MobState, Scene, type SpecialView } from '../shared/types';
import {
  CHAMPION_AFFIXES, MobSpecial, SPECIAL_RT, SpecialEv, championGold, championName, forcedSpecialPlan, goldGoblinGold, goldGoblinStones, rollSpecialPlan,
  type SpecialPlan,
} from '../shared/specialMonster';
import type { SpecialKind } from '../shared/content/special';
import type { GameSim } from './game';

const TICK_HZ = 60;
/** 도망 방향 후보(영웅 반대 방향에서 벌어진 각, rad) */
const FLEE_TRY = [0, 0.6, -0.6, 1.2, -1.2, 1.9, -1.9, 2.6, -2.6] as const;
/** 도망 방향을 다시 고르는 간격(세계 틱). 매 틱 바꾸면 벽 앞에서 떨린다 */
const FLEE_RETHINK = 6;
/** 황금 고블린 배회(미발견) 속도 배율·방향 바꾸는 간격(mobs.ts 배회와 같다) */
const WANDER_SPEED = 0.15;
const WANDER_TICKS = 120;

/** 알림 띠·이름표·오라가 읽는 한 판 상태 */
export class SpecialState implements SpecialView {
  phase = 0;
  kind = 0;
  mob = -1;
  fleeing = false;
  remainTicks = 0;
  affix = 0;
  name = '';
  serial = 0;
  bannerTicks = 0;
  planRoom = -1;
  rewardGold = 0;
  rewardStones = 0;
  rewardSerial = 0;
  /** 판 시작: 출현·보상 번호는 단조 증가라 유지한다(UI가 새 출현을 알아채게) */
  reset(): void {
    this.phase = 0; this.kind = 0; this.mob = -1; this.fleeing = false; this.remainTicks = 0; this.affix = 0; this.name = '';
    this.bannerTicks = 0; this.planRoom = -1; this.rewardGold = 0; this.rewardStones = 0;
  }
}

export class SpecialController {
  readonly state = new SpecialState();
  /** DEV·QA 강제 출현(?special=goldGoblin|champion). 일반 스테이지에서만 먹는다(보스판 금지는 그대로) */
  force: SpecialKind | null = null;
  /** 이번 판 계획(없으면 null) */
  plan: SpecialPlan | null = null;
  /**
   * 이번 판에 처치 순간 영속 지급한 스페셜 골드(장비 골드 획득 옵션 배율 적용 뒤 실제 지급액). 결과 화면 '스페셜 +N' 항목이 읽는다.
   * 클리어 골드(result.gold)에는 더하지 않는다 — 처치 때 이미 저장(save.addGold)해서 사망·귀환서·마을 복귀로도 사라지지 않고, 이중 지급도 없다.
   */
  paidGold = 0;
  /** 이번 판 스페셜 보상을 이미 지급했는지(한 판 최대 1마리 + 같은 처치 재호출 방지) */
  private rewarded = false;
  private runSerial = 0;
  private rng: SeedRng = seedRng(1);
  private readonly rand = (): number => nextRand(this.rng);
  /** 전투방 순번 → 판 계획 방 인덱스 */
  private readonly battleRooms = new Int16Array(8);
  private battleCount = 0;
  /** 챔피언이 승격되는 방·웨이브·그 웨이브의 승격 후보 수·지금까지 본 후보 수·승격 순번 */
  private champRoom = -1;
  private champWave = -1;
  private champTarget = 0;
  private champSeen = 0;
  /** 황금 고블린이 나올 방에 들어와 있던 세계 틱 */
  private roomTicks = 0;
  /** 황금 고블린이 나올 지도 방 인덱스(예정 방이 먼저 정리되면 다음 전투방으로 옮긴다) */
  private goblinRoom = -1;
  private tries = 0;
  private fleeAngle = 0;
  private fleeClock = 0;
  private burnClock = 0;
  private regionBase = 0;

  constructor(private readonly g: GameSim, private readonly seed: number) {}

  get active(): boolean { return this.plan !== null; }

  /**
   * 판 시작(GameSim.resetRun, loadMap 안): 계획을 굴린다. 스테이지 판(plan.stage 있음)의 일반 스테이지만 나온다.
   * 같은 (게임 씨앗, 판 번호, 스테이지)면 같은 계획이다.
   */
  begin(): void {
    const g = this.g, fp = g.flowController.plan, st = this.state;
    st.reset();
    this.plan = null; this.paidGold = 0; this.rewarded = false; this.battleCount = 0; this.champRoom = -1; this.champWave = -1; this.champTarget = 0; this.champSeen = 0;
    this.roomTicks = 0; this.tries = 0; this.fleeClock = 0; this.burnClock = 0; this.goblinRoom = -1;
    this.runSerial++;
    if (g.map.id === 'village' || fp.stage === null || fp.kind !== 'normal') return;
    for (let i = 0; i < fp.rooms.length && this.battleCount < this.battleRooms.length; i++) if (fp.rooms[i].kind === 'battle') this.battleRooms[this.battleCount++] = i;
    if (this.battleCount === 0) return;
    // 출현 확률 난이도는 판 계획의 실제 난이도(하드 판은 하드 확률) — 지역 기본 난이도(LOOT_MAP)가 아니다
    const diff: DifficultyKey = fp.diff;
    const seed = hashSeed(`${this.seed}:${this.runSerial}:${fp.stage}:special`);
    const plan = this.force ? forcedSpecialPlan(seed, this.force, diff, this.battleCount, TICK_HZ) : rollSpecialPlan(seed, 'normal', diff, this.battleCount, TICK_HZ);
    if (plan.kind === null) return;
    this.plan = plan;
    this.goblinRoom = this.battleRooms[plan.room];
    st.planRoom = plan.kind === 'goldGoblin' ? this.goblinRoom : this.battleRooms[this.battleCount - 1];
    this.rng = seedRng(plan.aux);
    this.regionBase = REGIONS[fp.region].reward.base;
    if (plan.kind === 'champion') {
      // 마지막 전투방(일반 스테이지는 아레나 수비대)의 마지막 웨이브에서, 방패·멧돼지·고블린(주술사·이미 정예인 몹 제외) 중 한 마리를 승격한다
      this.champRoom = this.battleRooms[this.battleCount - 1];
      const waves = fp.rooms[this.champRoom].waves;
      this.champWave = waves.length - 1;
      const w = waves[this.champWave];
      const eligible = Math.max(0, (w.counts[0] ?? 0) + (w.counts[1] ?? 0) + (w.counts[2] ?? 0) - (w.elites ?? 0));
      if (eligible === 0) { this.plan = null; return; }
      this.champTarget = Math.min(eligible - 1, Math.floor(plan.pick * eligible));
    }
  }

  /** mobController.spawn 끝: 챔피언 승격 판정(후보 순서가 목표 순번이면 이 몹이 챔피언) */
  onSpawn(slot: number, kind: number, elite: boolean, wave: number): void {
    const p = this.plan, st = this.state;
    if (!p || p.kind !== 'champion' || st.phase !== 0 || wave < 0) return;
    const g = this.g, f = g.flow;
    if (f.roomIndex !== this.champRoom || wave !== this.champWave) return;
    if (kind === MobKind.Shaman || elite) return;
    if (this.champSeen++ !== this.champTarget) return;
    const m = g.mobs;
    m.special[slot] = MobSpecial.Champion; m.affix[slot] = p.affix; m.elite[slot] = 1;
    m.hp[slot] = m.maxHp[slot] = Math.round(MOB_STATS[kind].hp * g.mobHpMult * SPECIAL.champion.hpMult);
    st.phase = 1; st.kind = MobSpecial.Champion; st.mob = slot; st.affix = p.affix; st.serial++;
    st.name = championName(p.affix, REGIONS[g.flowController.plan.region].mobNames[kind] ?? MOB_STATS[kind].name);
    st.bannerTicks = SPECIAL_RT.championBannerSec * TICK_HZ;
    this.burnClock = 0;
    const e = g.emit(SpecialEv.Appear); e.x = m.x[slot]; e.z = m.z[slot]; e.a = MobSpecial.Champion; e.b = slot; e.c = p.affix; e.d = 0;
  }

  /** 세계 스텝 1회(GameSim.advanceWorld, 몹 이동 뒤). 정수 타이머는 세계 시계가 1틱 넘어갔을 때만 올린다 */
  step(): void {
    const p = this.plan, st = this.state, g = this.g;
    if (!p || st.phase === 2 || g.flow.scene !== Scene.Dungeon || g.hero.hp <= 0) return;
    const whole = g.worldTick;
    if (p.kind === 'goldGoblin') {
      if (st.phase === 0) { if (whole) this.maybeSpawnGoblin(); return; }
      this.stepGoblin(whole);
    } else if (st.phase === 1) this.stepChampion(whole);
  }

  // ── 황금 고블린 ─────────────────────────────────────

  private maybeSpawnGoblin(): void {
    const g = this.g, f = g.flow;
    // 예정 방이 출현 전에 정리됐으면(빠른 정리) 다음 전투방으로 옮긴다. 더 뒤에 전투방이 없으면 정리 직전 훅(spawnBeforeClear)이 이미 불렀다
    if (this.goblinRoom >= 0 && f.roomState[this.goblinRoom] === 2) this.retargetGoblinRoom();
    if (f.roomIndex !== this.goblinRoom || f.roomState[f.roomIndex] !== 1) return;
    if (this.roomTicks++ < this.plan!.delayTicks) return;
    // 방 안 영웅에게서 spawnDist 떨어진 걸을 수 있는 자리(자리가 없으면 몇 틱 뒤 다시)
    if (this.tries++ % 15 !== 0) return;
    this.spawnGoblin(false);
  }

  /** 예정 방을 이미 정리했으면 그 뒤의 아직 정리하지 않은 전투방으로 옮긴다(방 안에서 흐른 시간은 다시 센다) */
  private retargetGoblinRoom(): void {
    const f = this.g.flow;
    for (let k = 0; k < this.battleCount; k++) {
      const room = this.battleRooms[k];
      if (room > this.goblinRoom && f.roomState[room] !== 2) { this.goblinRoom = room; this.state.planRoom = room; this.roomTicks = 0; return; }
    }
  }

  /**
   * 방 정리 직전(flow.update: 마지막 웨이브가 비어 곧 정리되는 틱)에 부른다. 예정 방에서 출현 시점이 오기 전에 정리되는 경우라
   * 황금 고블린을 지금 바로 내보낸다. 나왔으면 true(고블린이 살아 있어 이번 틱에는 정리하지 않는다). 계획이 없거나 이미 나왔으면 false.
   */
  spawnBeforeClear(): boolean {
    const p = this.plan, st = this.state, g = this.g, f = g.flow;
    if (!p || p.kind !== 'goldGoblin' || st.phase !== 0 || g.flow.scene !== Scene.Dungeon || g.hero.hp <= 0) return false;
    if (f.roomIndex !== this.goblinRoom || f.roomState[f.roomIndex] !== 1) return false;
    return this.spawnGoblin(true);
  }

  /** spawnBeforeClear와 같은 조건이지만 내보내지 않고 묻기만 한다(KO·클리어 연출 대기 판정용: 스페셜이 아직 나올 차례면 판이 끝나지 않는다) */
  dueBeforeClear(): boolean {
    const p = this.plan, st = this.state, g = this.g, f = g.flow;
    if (!p || p.kind !== 'goldGoblin' || st.phase !== 0 || f.scene !== Scene.Dungeon || g.hero.hp <= 0) return false;
    return f.roomIndex === this.goblinRoom && f.roomState[f.roomIndex] === 1;
  }

  /** 방 안 자리를 골라 황금 고블린을 낸다. immediate면 거리 조건을 완화해서라도 반드시 내본다 */
  private spawnGoblin(immediate: boolean): boolean {
    const g = this.g, f = g.flow;
    const area = g.map.rooms[f.roomIndex].area, ins = 2, h = g.hero;
    const r = this.rand;
    let x = h.x, z = h.z, ok = false;
    const attempts = immediate ? 64 : 24;
    for (let t = 0; t < attempts && !ok; t++) {
      x = area.x0 + ins + r() * Math.max(0.1, area.x1 - area.x0 - ins * 2);
      z = area.z0 + ins + r() * Math.max(0.1, area.z1 - area.z0 - ins * 2);
      const d = Math.hypot(x - h.x, z - h.z);
      // 즉시 출현은 앞 24번만 정상 거리, 이후에는 최소 거리만 지킨다(영웅 바로 위에 겹쳐 나오지 않게)
      const lo = SPECIAL_RT.spawnDist[0], hi = SPECIAL_RT.spawnDist[1];
      const inRange = t < 24 ? d >= lo && d <= hi : d >= Math.min(lo, 4);
      ok = inRange && g.walkable(x, z, MOB_STATS[MobKind.Goblin].radius, -1, true);
    }
    if (!ok) return false;
    const slot = g.flowController.spawnNear(MobKind.Goblin, false, x, z, -1);
    if (slot < 0) return false;
    const m = g.mobs;
    m.special[slot] = MobSpecial.GoldGoblin;
    // 알림과 목표 숫자: 처치하면 처치 수가 오르므로 목표에도 한 마리를 더한다(소멸하면 뺀다)
    f.objectiveTotal++;
    const st = this.state;
    st.phase = 1; st.kind = MobSpecial.GoldGoblin; st.mob = slot; st.fleeing = false; st.serial++; st.name = '황금 고블린';
    st.remainTicks = SPECIAL_RT.appearSec * TICK_HZ; st.bannerTicks = 0;
    this.fleeClock = 0;
    const e = g.emit(SpecialEv.Appear); e.x = m.x[slot]; e.z = m.z[slot]; e.a = MobSpecial.GoldGoblin; e.b = slot; e.c = 0; e.d = SPECIAL_RT.appearSec;
    return true;
  }

  /** 슬롯이 아직 이 스페셜 몹인지(처치·소멸·재사용 뒤에는 아님) */
  private alive(slot: number, special: number): boolean {
    if (slot < 0) return false;
    const m = this.g.mobs, s = m.state[slot];
    return m.special[slot] === special && s !== MobState.Inactive && s !== MobState.Dying;
  }

  private stepGoblin(whole: boolean): void {
    const st = this.state, g = this.g, m = g.mobs, i = st.mob;
    if (!this.alive(i, MobSpecial.GoldGoblin)) {
      // 처치(onKilled가 phase를 2로 이미 올렸다)나 방 정리로 사라진 경우: 끝
      if (st.phase === 1) this.finish();
      return;
    }
    this.checkHit(i);
    if (!whole) return;
    if (--st.remainTicks <= 0) this.vanish(i);
  }

  /** 처음 맞았으면 도망 상태로 바꾸고 시간을 도망 시간으로 다시 센다(한 번만) */
  private checkHit(i: number): void {
    const st = this.state, m = this.g.mobs;
    if (st.fleeing || m.hp[i] >= m.maxHp[i]) return;
    st.fleeing = true;
    st.remainTicks = Math.round(SPECIAL.goldGoblin.fleeSec * TICK_HZ);
    this.fleeClock = 0;
    const e = this.g.emit(SpecialEv.Flee); e.x = m.x[i]; e.z = m.z[i]; e.a = i; e.b = SPECIAL.goldGoblin.fleeSec;
  }

  /** 12초 안에 못 잡았다: 땅속으로 사라진다(보상 없음). 목표 숫자에서 뺀다 */
  private vanish(i: number): void {
    const g = this.g, m = g.mobs;
    const e = g.emit(SpecialEv.Vanish); e.x = m.x[i]; e.z = m.z[i]; e.a = i;
    g.mobController.vanish(i);
    if (g.flow.objectiveTotal > g.flow.objectiveKilled) g.flow.objectiveTotal--;
    this.finish();
  }

  private finish(): void {
    const st = this.state;
    st.phase = 2; st.mob = -1; st.fleeing = false; st.remainTicks = 0; st.bannerTicks = 0;
  }

  /**
   * 황금 고블린 한 틱 이동(mobs.ts가 Idle·Chase 상태에서 부른다). 공격하지 않는다.
   * 맞기 전: 천천히 배회. 맞은 뒤: 영웅 반대 방향으로 ×1.3 속도, 벽·낭떠러지(걸을 수 없는 칸)는 보행 판정으로 피한다.
   */
  goblinStep(i: number, dx: number, dz: number, dist: number, dt: number, whole: boolean): void {
    const g = this.g, m = g.mobs, stats = MOB_STATS[MobKind.Goblin];
    this.checkHit(i);
    if (!this.state.fleeing) {
      if (m.state[i] !== MobState.Idle) { m.state[i] = MobState.Idle; m.stateTick[i] = 0; }
      if (whole && m.stateTick[i] % WANDER_TICKS === 0) m.facing[i] = this.rand() * Math.PI * 2 - Math.PI;
      const sp = stats.speed * WANDER_SPEED * dt;
      g.moveMob(i, Math.sin(m.facing[i]) * sp, Math.cos(m.facing[i]) * sp, stats.radius);
      return;
    }
    if (m.state[i] !== MobState.Chase) { m.state[i] = MobState.Chase; m.stateTick[i] = 0; }
    if (whole && this.fleeClock++ % FLEE_RETHINK === 0) this.fleeAngle = this.pickFlee(i, dx, dz, dist);
    const a = this.fleeAngle, sp = stats.speed * SPECIAL.goldGoblin.speedMult * dt;
    m.facing[i] = a;
    g.moveMob(i, Math.sin(a) * sp, Math.cos(a) * sp, stats.radius);
  }

  /** 도망 방향: 영웅 반대 방향에서 가까운 각부터, 앞(fleeLook m)이 걸을 수 있고 영웅과 가장 멀어지는 방향 */
  private pickFlee(i: number, dx: number, dz: number, dist: number): number {
    const g = this.g, m = g.mobs, radius = MOB_STATS[MobKind.Goblin].radius;
    const base = dist > 1e-6 ? Math.atan2(-dx, -dz) : m.facing[i];
    let best = base, bestScore = -Infinity;
    for (let k = 0; k < FLEE_TRY.length; k++) {
      const a = base + FLEE_TRY[k];
      const px = m.x[i] + Math.sin(a) * SPECIAL_RT.fleeLook, pz = m.z[i] + Math.cos(a) * SPECIAL_RT.fleeLook;
      if (!g.walkable(px, pz, radius, -1, true)) continue;
      // 두 걸음 앞도 열려 있으면 가점(막다른 길로 몰리지 않게)
      const qx = m.x[i] + Math.sin(a) * SPECIAL_RT.fleeLook * 2.2, qz = m.z[i] + Math.cos(a) * SPECIAL_RT.fleeLook * 2.2;
      const open = g.walkable(qx, qz, radius, -1, true) ? 1 : 0;
      const score = Math.hypot(px - g.hero.x, pz - g.hero.z) + open * 1.5 - Math.abs(FLEE_TRY[k]) * 0.35;
      if (score > bestScore) { bestScore = score; best = a; }
    }
    return best;
  }

  // ── 챔피언 ─────────────────────────────────────────

  private stepChampion(whole: boolean): void {
    const st = this.state, g = this.g, m = g.mobs, i = st.mob, h = g.hero;
    if (!this.alive(i, MobSpecial.Champion)) { if (st.phase === 1) this.finish(); return; }
    if (!whole) return;
    if (st.bannerTicks > 0) st.bannerTicks--;
    // 불꽃 오라: 가까이 서 있으면 intervalSec마다 작은 화상(실제 피해, 받는 피해 배율·보호막 적용). 등장 연출이 끝난 뒤부터
    const burn = CHAMPION_AFFIXES[m.affix[i]]?.burn;
    if (burn && m.state[i] !== MobState.Spawning) {
      if (++this.burnClock >= burn.intervalSec * TICK_HZ) {
        this.burnClock = 0;
        if (Math.hypot(h.x - m.x[i], h.z - m.z[i]) <= burn.radius) g.combat.burnHero(Math.max(1, Math.round(h.maxHp * burn.ratio)), 0);
      }
    }
  }

  // ── 처치 보상 ──────────────────────────────────────

  /**
   * 스페셜 몹 처치(combat.killMob가 일반 드롭 대신 부른다). 일반 드롭(몹 확률·엘리트 확정)은 그대로 굴리고 스페셜 보상을 더한다:
   * 황금 고블린 = 골드 base×1(처치 순간 영속 지급) + 강화석 1~3(바닥에 흩어짐), 챔피언 = 장비 확정(등급 가중 한 단계 위) + 골드 base×0.5.
   */
  onKilled(i: number): void {
    const g = this.g, m = g.mobs, st = this.state, k = m.special[i];
    if (k === MobSpecial.GoldGoblin) {
      g.loot.onMobKilled(m.x[i], m.z[i], false);
      const gold = goldGoblinGold(this.regionBase), stones = goldGoblinStones(this.rand);
      const paid = this.payGold(gold);
      g.loot.dropStones(m.x[i], m.z[i], stones);
      this.reward(i, k, paid, stones, 0);
    } else if (k === MobSpecial.Champion) {
      g.loot.onMobKilled(m.x[i], m.z[i], true, SPECIAL.champion.gearRarityStep);
      const gold = championGold(this.regionBase);
      const paid = this.payGold(gold);
      this.reward(i, k, paid, 0, SPECIAL.champion.gearRarityStep);
    } else return;
    if (st.phase === 1 && st.mob === i) this.finish();
  }

  private reward(i: number, kind: number, gold: number, stones: number, step: number): void {
    const g = this.g, m = g.mobs, st = this.state;
    st.rewardGold = gold; st.rewardStones = stones; st.rewardSerial++;
    const e = g.emit(SpecialEv.Reward); e.x = m.x[i]; e.z = m.z[i]; e.a = gold; e.b = stones; e.c = kind; e.d = step;
  }

  /**
   * 처치 순간 골드를 영속 지급한다(기존 보상 저장 경로 save.addGold: 미저장분은 성공할 때까지 증분으로 보관·재시도).
   * 장비 골드 획득 옵션 배율은 던전 입장 때 고정된 값을 그대로 쓴다(난이도·스테이지 배율은 곱하지 않는다).
   * 한 판에 한 번만 지급한다 — 스페셜 몹은 한 판 최대 1마리이고, 같은 처치가 다시 불려도 두 번 주지 않는다.
   * 실제 지급액(배율 적용 후)을 돌려준다 — 알림 띠·이벤트가 이 값을 보여 준다(PR #175 5차 리뷰 P2). 이미 지급했으면 그때 값.
   */
  private payGold(gold: number): number {
    if (this.rewarded) return this.paidGold;
    this.rewarded = true;
    const paid = Math.round(gold * this.g.stats.goldMult);
    this.paidGold = paid;
    if (paid > 0) this.g.save.addGold(paid);
    return paid;
  }
}
